import React, { useState, useEffect, useMemo } from "react";
import { useLanguage } from "./contexts/LanguageContext";
import { X, Sparkles, BadgePercent, Check, Package, Zap, Gem, Rocket } from 'lucide-react';
import {
  getPlanPriceEUR,
  getPlanTxLimit,
  isEarlyAdopterPricingActive,
  formatTxLimitDisplay,
} from "./utils/planUtils";

const WALLET_SLOTS = { free: "2", standard: "4", pro: "6", elite: "8", ultimate: "10" };

function buildPlansFromCatalog(language = "de") {
  const early = isEarlyAdopterPricingActive(
    typeof import.meta !== "undefined" ? import.meta.env : {}
  );
  const ids = ["free", "standard", "pro", "elite", "ultimate"];
  const names = {
    free: "Free",
    standard: "Standard",
    pro: "Pro",
    elite: "Elite",
    ultimate: "Ultimate",
  };

  return ids.map((id) => {
    const txLimit = getPlanTxLimit(id);
    const priceEUR = getPlanPriceEUR(id, { earlyAdopter: early });
    const regularPriceEUR = getPlanPriceEUR(id, { earlyAdopter: false });
    const txLabel =
      txLimit == null
        ? formatTxLimitDisplay(id, language === "en" ? "en" : "de")
        : txLimit.toLocaleString(language === "en" ? "en-US" : "de-DE");

    return {
      id,
      name: names[id],
      wallets: WALLET_SLOTS[id],
      transactions: txLabel,
      nftSupport: true,
      csvExport: id !== "free",
      pdfExport: id !== "free",
      priceEUR,
      regularPriceEUR,
      price:
        priceEUR === 0
          ? "0 €"
          : `${priceEUR.toLocaleString(language === "en" ? "en-US" : "de-DE", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })} €`,
    };
  });
}

const PlanModal = ({ isOpen, onClose, selectedYear = "2025", walletAddress, transactionCount, onPlanSelected, onPaymentMethodSelected }) => {
  const { language } = useLanguage();
  const plans = useMemo(() => buildPlansFromCatalog(language), [language]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("");
  const [error, setError] = useState(null);
  const [isMobileView, setIsMobileView] = useState(false);
  
  // 🚀 NEUE STATES: Automatische VeWorld-Transaktion
  const [selectedToken, setSelectedToken] = useState('vet');
  const [autoTransactionLoading, setAutoTransactionLoading] = useState(false);
  const [autoTransactionData, setAutoTransactionData] = useState(null);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedPlan(null);
      setSelectedPaymentMethod("");
      setError(null);
    }
  }, [isOpen]);

  useEffect(() => {
    const updateIsMobile = () => {
      if (typeof window === 'undefined') return;
      setIsMobileView(window.innerWidth < 768);
    };

    updateIsMobile();

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', updateIsMobile);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', updateIsMobile);
      }
    };
  }, []);

  // 🚀 NEUE FUNKTION: Automatische VeWorld-Transaktion starten
  const handleAutoTransaction = async (plan, year, walletCount) => {
    try {
      setAutoTransactionLoading(true);
      setError(null);
      
      console.log('🚀 Starte automatische Transaktion für:', { 
        plan, 
        year, 
        walletCount,
        token: selectedToken 
      });
      
      // 🎯 NEU: Nur IP abrufen, KEINE Pending-Order erstellen!
      // Die Pending-Order wird erst beim Klick auf "Transaktion starten" in App.jsx erstellt
      console.log('🔍 Nur IP abrufen - Pending-Order wird später erstellt');
      
      // 🎯 Direkte Weiterleitung an die Haupt-App statt veWorldPaymentAPI
      // Die Haupt-App übernimmt jetzt die DAppKit-Integration
      if (onPlanSelected) {
        onPlanSelected({ name: plan, wallets: walletCount });
      }
      if (onPaymentMethodSelected) {
        onPaymentMethodSelected(selectedToken);
      }
      onClose(); // Modal schließen
      console.log('✅ Transaktion an Haupt-App weitergeleitet (ohne Pending-Order)');
      
    } catch (error) {
      console.error('❌ Automatische Transaktion fehlgeschlagen:', error);
      setError(error.message);
    } finally {
      setAutoTransactionLoading(false);
    }
  };

  // Don't render if not open
  if (!isOpen) return null;

  const t = (key, lang) => {
    const translations = {
      'compare_plans': { 'en': 'Compare Plans', 'de': 'Pläne vergleichen' },
      'select_plan': { 'en': 'Select Plan', 'de': 'Plan auswählen' },
      'select_payment': { 'en': 'Select Payment Method', 'de': 'Zahlungsmethode auswählen' },
      'proceed_to_payment': { 'en': 'Proceed to Payment', 'de': 'Zur Zahlung' },
      'plan_selected': { 'en': 'Plan Selected', 'de': 'Plan ausgewählt' },
      'features': { 'en': 'Features', 'de': 'Funktionen' },
      'max_transactions': { 'en': 'Max. Transactions', 'de': 'Max. Transaktionen' },
      'airdrops_governance': { 'en': 'Airdrops, Governance Rewards, Stargate Staking', 'de': 'Airdrops, Governance Rewards, Stargate Staking' },
      'swap_classification': { 'en': 'Automatic Swap Classification & Calculation', 'de': 'Automatische Swap-Klassifizierung & Berechnung' },
      'nft_support': { 'en': 'NFT Tax Support (Galaxy Upgrades, NFT Purchase & Sale)', 'de': 'NFT-Steuerunterstützung (Galaxy Upgrades, NFT Kauf & Verkauf)' },
      'csv_export': { 'en': 'CSV Export', 'de': 'CSV Export' },
      'pdf_export': { 'en': 'PDF Export', 'de': 'PDF Export' },
      'price': { 'en': 'Price', 'de': 'Preis' },
      'plan_description': { 'en': 'All plans include full support for VET, VTHO, and B3TR tokens. NFT transactions and SWAPs are processed with precise FIFO tax logic.', 'de': 'Alle Pläne beinhalten vollständige Unterstützung für VET, VTHO und B3TR Token. NFT-Transaktionen und SWAPs werden mit präziser FIFO-Steuerlogik verarbeitet.' },
      'wallet_info': { 'en': 'Wallet Information', 'de': 'Wallet-Informationen' },
      'address': { 'en': 'Address', 'de': 'Adresse' },
      'year': { 'en': 'Year', 'de': 'Jahr' },
      'transactions': { 'en': 'Transactions', 'de': 'Transaktionen' },
      'recommended_plan': { 'en': 'Recommended Plan', 'de': 'Empfohlener Plan' },
      'payment_methods': { 'en': 'Payment Methods', 'de': 'Zahlungsmethoden' },
      'credit_card': { 'en': 'Credit Card', 'de': 'Kreditkarte' },
      'paypal': { 'en': 'PayPal', 'de': 'PayPal' },
      'crypto': { 'en': 'Crypto (VET)', 'de': 'Krypto (VET)' },
      'bank_transfer': { 'en': 'Bank Transfer', 'de': 'Banküberweisung' }
    };
    return translations[key]?.[lang] || key;
  };

  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
    setError(null);
    // 🚀 Plan an App.jsx weitergeben
    if (onPlanSelected) {
      onPlanSelected(plan);
    }
  };

  const handlePaymentSelect = (method) => {
    setSelectedPaymentMethod(method);
    setError(null);
    // 🚀 Zahlungsmethode an App.jsx weitergeben
    if (onPaymentMethodSelected) {
      onPaymentMethodSelected(method);
    }
  };

  const handleProceedToPayment = async () => {
    if (!selectedPlan) {
      setError('Please select a plan first');
      return;
    }
    if (!selectedPaymentMethod) {
      setError('Please select a payment method');
      return;
    }

    // 🚀 Krypto-Zahlungen direkt verarbeiten
    if (['vet', 'b3tr', 'vtho'].includes(selectedPaymentMethod)) {
      try {
        setError(null);
        console.log(`🚀 ${selectedPaymentMethod.toUpperCase()}-Zahlung wird verarbeitet...`);
        
        // Automatisch den entsprechenden Token setzen
        setSelectedToken(selectedPaymentMethod);
        
        // Automatische Transaktion starten
        await handleAutoTransaction(selectedPlan.name, selectedYear, 1); // walletCount = 1 für jetzt
        return; // Nicht schließen, da wir das automatische Transaktions-Modal öffnen
        
      } catch (error) {
        console.error(`❌ ${selectedPaymentMethod.toUpperCase()}-Zahlungsfehler:`, error);
        setError(`${selectedPaymentMethod.toUpperCase()}-Zahlungsfehler: ${error.message}`);
      }
      return;
    }



    // 🚀 Normale Zahlungsmethode (Stripe, etc.)
    console.log('✅ Plan and payment method selected:', selectedPlan.name, 'with method:', selectedPaymentMethod);
    
    // Callbacks aufrufen
    if (onPlanSelected) {
      onPlanSelected(selectedPlan);
    }
    if (onPaymentMethodSelected) {
      onPaymentMethodSelected(selectedPaymentMethod);
    }
    
    // Modal schließen
    onClose();
  };

  const getRecommendedPlan = () => {
    if (!transactionCount) return null;
    
    const count = parseInt(transactionCount);
    if (count <= 100) return 'free';
    if (count <= 1000) return 'standard';
    if (count <= 3000) return 'pro';
    if (count <= 10000) return 'elite';
    return 'ultimate';
  };

  const recommendedPlanId = getRecommendedPlan();

  const formatPrice = (n) => (n == null || !Number.isFinite(n) ? '0 €' : `${Number(n).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`);
  const getDiscountPercent = (plan) => {
    if (!plan?.regularPriceEUR || plan.regularPriceEUR <= 0 || plan.priceEUR >= plan.regularPriceEUR) return 0;
    return Math.round(((plan.regularPriceEUR - plan.priceEUR) / plan.regularPriceEUR) * 100);
  };
  const maxEarlyAdopterDiscount = plans.reduce((max, plan) => Math.max(max, getDiscountPercent(plan)), 0);
  const paidPlans = plans.filter((plan) => plan.regularPriceEUR > 0);
  const lowestRegularPaidPrice = paidPlans.length ? Math.min(...paidPlans.map((plan) => plan.regularPriceEUR)) : 0;
  const highestRegularPaidPrice = paidPlans.length ? Math.max(...paidPlans.map((plan) => plan.regularPriceEUR)) : 0;

  const renderPriceDisplay = (plan, compact = false) => {
    const hasDiscount =
      !!plan &&
      plan.priceEUR > 0 &&
      plan.regularPriceEUR > 0 &&
      plan.regularPriceEUR > plan.priceEUR;

    const discountPercent = hasDiscount ? getDiscountPercent(plan) : 0;
    const savings = hasDiscount ? plan.regularPriceEUR - plan.priceEUR : 0;

    const baseCardClass = compact
      ? 'relative flex h-full min-h-[146px] w-full flex-col justify-between overflow-hidden rounded-[22px] border px-3.5 py-3 text-center shadow-sm'
      : 'relative flex h-full min-h-[160px] w-full flex-col justify-between overflow-hidden rounded-[22px] border px-3.5 py-3 text-center shadow-sm';

    if (!hasDiscount) {
      return (
        <div className="h-full w-full">
          <div className={`${baseCardClass} border-slate-200 bg-gradient-to-br from-white via-slate-50 to-slate-100`}>
            <div className="pointer-events-none absolute -right-10 -top-10 h-20 w-20 rounded-full bg-emerald-200/20 blur-3xl" />
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.55),transparent_36%)]" />

            <div className="relative flex justify-center">
              <div className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-1 text-[9px] font-semibold uppercase tracking-[0.16em] text-emerald-700">
                <Check className="h-3 w-3" />
                {language === 'en' ? 'Included' : 'Inklusive'}
              </div>
            </div>

            <div className="relative mt-3">
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                {language === 'en' ? 'Starter Access' : 'Starter-Zugang'}
              </div>
              <div className="mt-1.5 text-[1.75rem] font-bold leading-none tracking-tight text-slate-900">
                {plan?.price || formatPrice(0)}
              </div>
            </div>

            <div className="relative mt-3 border-t border-slate-200/80 pt-3">
              <div className="text-[9px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                {language === 'en' ? 'Regular Pricing' : 'Regulärer Preis'}
              </div>
              <div className="mt-1 text-sm font-medium text-slate-400">
                {language === 'en' ? 'Always free' : 'Dauerhaft kostenlos'}
              </div>
            </div>

            <div className="relative mt-3 inline-flex self-center rounded-full border border-slate-200 bg-white/90 px-2 py-1 text-[10px] font-semibold text-slate-600">
              {language === 'en' ? 'Best for smaller wallets' : 'Ideal für kleinere Wallets'}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="h-full w-full">
        <div className={`${baseCardClass} border-slate-200/90 bg-gradient-to-br from-white via-slate-50 to-blue-50 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md`}>
          <div className="pointer-events-none absolute -right-10 -top-10 h-20 w-20 rounded-full bg-amber-200/30 blur-3xl" />
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.6),transparent_36%)]" />

          <div className="relative flex justify-center">
            <div className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50/95 px-2 py-1 text-[9px] font-semibold uppercase tracking-[0.16em] text-amber-700">
              <BadgePercent className="h-3 w-3" />
              <span>{language === 'en' ? 'Early Adopter' : 'Early Adopter'} · -{discountPercent}%</span>
            </div>
          </div>

          <div className="relative mt-3">
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-blue-600/80">
              {language === 'en' ? 'Launch Price' : 'Launch-Preis'}
            </div>
            <div className="mt-1.5 text-[1.75rem] font-bold leading-none tracking-tight text-slate-900">
              {formatPrice(plan.priceEUR)}
            </div>
          </div>

          <div className="relative mt-3 border-t border-slate-200/80 pt-3">
            <div className="text-[9px] font-semibold uppercase tracking-[0.16em] text-slate-400">
              {language === 'en' ? 'Regular Pricing' : 'Regulärer Preis'}
            </div>
            <div className="mt-1 text-sm font-medium text-slate-400 line-through">
              {formatPrice(plan.regularPriceEUR)}
            </div>
          </div>

          <div className="relative mt-3 inline-flex self-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-1 text-[10px] font-semibold text-emerald-700">
            {language === 'en' ? `Save ${formatPrice(savings)}` : `${formatPrice(savings)} sparen`}
          </div>
        </div>
      </div>
    );
  };

  const planTheme = {
    free: {
      Icon: Check,
      badge: 'from-emerald-500 via-green-600 to-lime-500',
      ring: 'ring-emerald-200/70',
      soft: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      subtitle: language === 'en' ? 'Starter' : 'Einstieg',
    },
    standard: {
      Icon: Package,
      badge: 'from-slate-500 via-slate-600 to-sky-600',
      ring: 'ring-slate-200/70',
      soft: 'bg-slate-50 text-slate-700 border-slate-200',
      subtitle: language === 'en' ? 'Core' : 'Core',
    },
    pro: {
      Icon: Zap,
      badge: 'from-[#1E3A8A] via-[#4338CA] to-[#7266FF]',
      ring: 'ring-indigo-200/70',
      soft: 'bg-indigo-50 text-indigo-700 border-indigo-200',
      subtitle: language === 'en' ? 'Advanced' : 'Advanced',
    },
    elite: {
      Icon: Gem,
      badge: 'from-amber-400 via-yellow-500 to-orange-500',
      ring: 'ring-amber-200/70',
      soft: 'bg-amber-50 text-amber-700 border-amber-200',
      subtitle: language === 'en' ? 'Premium' : 'Premium',
    },
    ultimate: {
      Icon: Rocket,
      badge: 'from-red-500 via-rose-600 to-orange-500',
      ring: 'ring-rose-200/70',
      soft: 'bg-rose-50 text-rose-700 border-rose-200',
      subtitle: language === 'en' ? 'Flagship' : 'Flagship',
    },
  };

  const getPlanIcon = (planId, size = 'md') => {
    const theme = planTheme[planId] || planTheme.free;
    const Icon = theme.Icon;

    const sizeMap = {
      sm: {
        wrap: 'h-8 w-8 rounded-xl',
        icon: 'h-4 w-4',
      },
      md: {
        wrap: 'h-10 w-10 rounded-2xl',
        icon: 'h-5 w-5',
      },
    };

    const current = sizeMap[size] || sizeMap.md;

    return (
      <div
        className={`relative inline-flex items-center justify-center overflow-hidden bg-gradient-to-br ${theme.badge} ${current.wrap} ring-1 ${theme.ring} shadow-sm`}
      >
        <div className="absolute inset-0 rounded-[inherit] bg-gradient-to-b from-white/30 to-transparent opacity-60" />
        <Icon className={`${current.icon} relative z-10 text-white drop-shadow-sm`} />
      </div>
    );
  };

  const renderPlanHeader = (plan) => {
    const theme = planTheme[plan.id] || planTheme.free;
    const isRecommended = recommendedPlanId === plan.id;

    return (
      <div className="flex min-h-[70px] flex-col items-center justify-center gap-1.5 rounded-2xl border border-slate-200/90 bg-white/90 px-2.5 py-2.5 shadow-sm">
        {getPlanIcon(plan.id)}

        <div className="text-center leading-tight">
          <div className="text-xs font-semibold text-slate-900">{plan.name}</div>
          <div className="mt-0.5 text-[9px] font-semibold uppercase tracking-[0.16em] text-slate-500">
            {theme.subtitle}
          </div>
        </div>

        {isRecommended && (
          <span className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.14em] text-blue-700">
            {language === 'en' ? 'Recommended' : 'Empfohlen'}
          </span>
        )}
      </div>
    );
  };

  const paymentMethods = [
    { id: 'credit_card', name: t('credit_card', language), icon: '💳' },
    { id: 'vet', name: '🔗 VET (VeChain)', icon: '🔗' },
    { id: 'b3tr', name: '🪙 B3TR', icon: '🪙' },
    { id: 'vtho', name: '⚡ VTHO', icon: '⚡' },
    { id: 'paypal', name: t('paypal', language), icon: '📱' },
    { id: 'bank_transfer', name: t('bank_transfer', language), icon: '🏦' }
  ];

  const featureItems = [
    {
      key: 'transactions',
      label: t('max_transactions', language),
      render: (plan) => plan.transactions,
    },
    {
      key: 'airdrops',
      label: t('airdrops_governance', language),
      render: () => '✅',
    },
    {
      key: 'swap',
      label: t('swap_classification', language),
      render: () => '✅',
    },
    {
      key: 'nft',
      label: t('nft_support', language),
      render: (plan) => (plan.nftSupport ? '✅' : '❌'),
    },
    {
      key: 'csv',
      label: t('csv_export', language),
      render: (plan) => (plan.csvExport ? '✅' : '❌'),
    },
    {
      key: 'pdf',
      label: t('pdf_export', language),
      render: (plan) => (plan.pdfExport ? '✅' : '❌'),
    },
    {
      key: 'price',
      label: (
        <div className="flex flex-col">
          <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-600/80">
            {language === 'en' ? 'Launch Offer' : 'Launch-Angebot'}
          </span>
          <span className="mt-1 text-sm font-semibold text-slate-900">
            {t('price', language)}
          </span>
        </div>
      ),
      render: (plan) => renderPriceDisplay(plan, false),
    },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 backdrop-blur-[3px] px-3 py-3 md:px-6 md:py-4 overflow-y-auto md:overflow-hidden">
      <div
        className="bg-white rounded-[30px] shadow-[0_30px_90px_-25px_rgba(15,23,42,0.42)] border border-white/70 w-[min(96vw,1500px)] overflow-hidden flex flex-col"
        style={isMobileView ? { maxHeight: 'calc(100vh - 24px)' } : { maxHeight: 'calc(100vh - 32px)' }}
      >
        <div className="relative z-10 bg-white border-b border-slate-200 px-6 md:px-7 py-3.5 flex items-center justify-between gap-4">
          <div>
            <h2 className="text-lg md:text-[1.35rem] font-bold text-slate-900">
              {t('compare_plans', language)}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {language === 'en'
                ? 'Compare features, limits and current launch pricing'
                : 'Vergleiche Funktionen, Limits und die aktuelle Launch-Preisphase'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
            aria-label={language === 'en' ? 'Close modal' : 'Modal schließen'}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 md:px-7 pt-3 pb-4 overflow-y-auto md:overflow-visible">

        {/* Wallet Information */}
        {walletAddress && (
          <div className="bg-blue-50 p-4 rounded-lg mb-6">
            <h3 className="font-semibold text-blue-800 mb-2">{t('wallet_info', language)}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="font-medium">{t('address', language)}:</span>
                <div className="font-mono text-blue-600">{walletAddress.slice(0, 8)}...{walletAddress.slice(-6)}</div>
              </div>
              <div>
                <span className="font-medium">{t('year', language)}:</span>
                <div className="text-blue-600">{selectedYear}</div>
              </div>
              <div>
                <span className="font-medium">{t('transactions', language)}:</span>
                <div className="text-blue-600">{transactionCount || 'Unknown'}</div>
              </div>
            </div>
            {recommendedPlanId && (
              <div className="mt-2 text-sm">
                <span className="font-medium">{t('recommended_plan', language)}:</span>
                <span className="ml-2 text-blue-600 font-semibold">
                  {plans.find(p => p.id === recommendedPlanId)?.name}
                </span>
              </div>
            )}
          </div>
        )}

        <div className="text-slate-600 text-sm mb-3">
          <p>{t('plan_description', language)}</p>
        </div>

        {/* Premium Early-Adopter Banner */}
        <div className="relative mb-4 overflow-hidden rounded-[26px] border border-slate-200 bg-gradient-to-r from-slate-950 via-blue-950 to-slate-900 px-5 py-3.5 shadow-[0_18px_50px_-20px_rgba(15,23,42,0.55)]">
          <div className="pointer-events-none absolute -right-10 -top-10 h-36 w-36 rounded-full bg-amber-300/20 blur-3xl" />
          <div className="pointer-events-none absolute -left-10 bottom-0 h-28 w-28 rounded-full bg-blue-400/10 blur-3xl" />

          <div className="relative flex flex-col gap-2.5 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-amber-200 backdrop-blur-sm">
                <Sparkles className="h-3.5 w-3.5" />
                {language === 'en' ? 'Early-Adopter Discount' : 'Early-Adopter Rabatt'}
              </div>

              <h3 className="mt-2.5 text-base md:text-[1.05rem] font-semibold leading-tight text-white">
                {language === 'en'
                  ? `Launch pricing is currently active — save up to ${maxEarlyAdopterDiscount}% before regular pricing starts.`
                  : `Aktuell ist die Launch-Preisphase aktiv — spare bis zu ${maxEarlyAdopterDiscount} %, bevor die regulären Preise gelten.`}
              </h3>

              <p className="mt-1.5 text-[13px] leading-5 text-blue-100/85">
                {language === 'en'
                  ? 'These prices are currently available as a limited Early-Adopter offer. You secure the reduced launch price today; regular pricing will apply later.'
                  : 'Diese Preise gelten aktuell als limitiertes Early-Adopter-Angebot. Sie sichern sich heute den reduzierten Launch-Preis; später gelten die regulären Preise.'}
              </p>
            </div>

            <div className="grid gap-2.5 sm:grid-cols-2 lg:w-[330px]">
              <div className="rounded-2xl border border-white/12 bg-white/10 px-3.5 py-2.5 backdrop-blur-sm">
                <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-blue-100/70">
                  {language === 'en' ? 'Regular Pricing' : 'Reguläre Preise'}
                </div>
                <div className="mt-1 text-sm font-semibold text-white">
                  {formatPrice(lowestRegularPaidPrice)} – {formatPrice(highestRegularPaidPrice)}
                </div>
              </div>

              <div className="rounded-2xl border border-amber-300/25 bg-amber-300/10 px-3.5 py-2.5 backdrop-blur-sm">
                <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-amber-100/80">
                  {language === 'en' ? 'Launch Advantage' : 'Launch-Vorteil'}
                </div>
                <div className="mt-1 text-sm font-semibold text-white">
                  {language === 'en' ? `Up to ${maxEarlyAdopterDiscount}% off` : `Bis zu ${maxEarlyAdopterDiscount} % günstiger`}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Plan Comparison Table - Desktop */}
        <div className="hidden md:block mb-6">
          <table className="w-full table-fixed text-[13px] border-separate border-spacing-0 rounded-2xl overflow-hidden border border-slate-200 bg-white">
            <thead>
              <tr className="bg-gradient-to-r from-slate-50 via-blue-50 to-slate-50 text-left">
                <th className="w-[24%] p-4 font-semibold text-slate-800 align-middle">
                  <div className="flex flex-col">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-600/80">
                      {language === 'en' ? 'Comparison' : 'Vergleich'}
                    </span>
                    <span className="mt-1 text-sm font-semibold text-slate-900">
                      {t('features', language)}
                    </span>
                  </div>
                </th>
                {plans.map((plan) => (
                  <th key={plan.name} className="p-3 font-semibold text-center align-top">
                    {renderPlanHeader(plan)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {featureItems.map((feature) => {
                const isPriceRow = feature.key === 'price';

                return (
                  <tr
                    key={feature.key}
                    className={`border-t border-slate-200 ${
                      isPriceRow ? 'bg-slate-50/80 align-top' : 'bg-white'
                    }`}
                  >
                    <td
                      className={`${
                        isPriceRow
                          ? 'p-3.5 align-top font-semibold text-slate-900'
                          : 'px-4 py-2.5 text-slate-700'
                      }`}
                    >
                      {feature.label}
                    </td>

                    {plans.map((plan) => (
                      <td
                        key={plan.id + feature.key}
                        className={`${
                          isPriceRow
                            ? 'p-1.5 align-top'
                            : 'px-4 py-2.5 text-center text-slate-700'
                        }`}
                      >
                        {feature.render(plan)}
                      </td>
                    ))}
                  </tr>
                );
              })}
              <tr className="border-t border-slate-200 bg-white/95">
                <td className="p-3"></td>
                {plans.map((plan) => (
                  <td key={plan.id + 'select'} className="p-1.5 pb-2.5 text-center">
                    <button
                      onClick={() => handlePlanSelect(plan)}
                      className={`w-full rounded-2xl px-4 py-2.5 font-semibold transition-all duration-200 ${
                        selectedPlan?.id === plan.id
                          ? 'bg-blue-600 text-white shadow-lg shadow-blue-200 ring-2 ring-blue-300'
                          : 'border border-slate-200 bg-white text-slate-700 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700'
                      }`}
                    >
                      {selectedPlan?.id === plan.id
                        ? t('plan_selected', language)
                        : t('select_plan', language)}
                    </button>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>

        {/* Mobile Plan Cards */}
        <div className="grid gap-4 md:hidden mb-8">
          {plans.map((plan) => {
            const isSelected = selectedPlan?.id === plan.id;
            const isRecommended = recommendedPlanId === plan.id;

            return (
              <div
                key={plan.id}
                className={`rounded-2xl border transition-all duration-200 ${
                  isSelected ? 'border-blue-400 shadow-lg' : 'border-gray-200 shadow-sm'
                }`}
              >
                <div className="p-5 space-y-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-3">
                        {getPlanIcon(plan.id)}
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{plan.name}</h3>
                          <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                            {(planTheme[plan.id] || planTheme.free).subtitle}
                          </div>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {language === 'en'
                          ? 'Full support for VET, VTHO & B3TR tokens'
                          : 'Volle Unterstützung für VET, VTHO & B3TR Tokens'}
                      </p>
                    </div>
                    <div className="text-right">
                      {renderPriceDisplay(plan, true)}
                      {isRecommended && (
                        <div className="text-[10px] mt-1 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-blue-50 text-blue-600 font-semibold uppercase tracking-wide">
                          ⭐ {t('recommended_plan', language)}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-gray-700">
                    {featureItems
                      .filter((feature) => feature.key !== 'price')
                      .map((feature) => (
                        <div
                          key={feature.key}
                          className="flex flex-col rounded-lg border border-gray-100 bg-gray-50 px-3 py-2"
                        >
                          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                            {feature.label}
                          </span>
                          <span className="mt-1 text-sm font-medium text-gray-900">
                            {feature.render(plan)}
                          </span>
                        </div>
                      ))}
                  </div>

                  <button
                    onClick={() => handlePlanSelect(plan)}
                    className={`w-full px-4 py-3 rounded-lg font-semibold transition-all duration-200 ${
                      isSelected
                        ? 'bg-blue-600 text-white shadow-lg'
                        : 'bg-white text-blue-600 border border-blue-200 hover:bg-blue-50'
                    }`}
                  >
                    {isSelected ? t('plan_selected', language) : t('select_plan', language)}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Payment Method Selection */}
        {selectedPlan && (
          <div className="mb-5 rounded-[24px] border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h3 className="text-base font-semibold text-slate-900">
                  {language === 'en' ? 'Choose payment method' : 'Zahlungsmethode wählen'}
                </h3>
                <p className="mt-1 text-sm text-slate-500">
                  {language === 'en'
                    ? `Selected plan: ${selectedPlan.name}`
                    : `Ausgewählter Plan: ${selectedPlan.name}`}
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2.5 lg:min-w-[720px]">
                {paymentMethods.map((method) => (
                  <button
                    key={method.id}
                    onClick={() => handlePaymentSelect(method.id)}
                    className={`rounded-2xl border px-3 py-3 text-center transition-all duration-200 ${
                      selectedPaymentMethod === method.id
                        ? 'border-blue-500 bg-blue-50 shadow-sm'
                        : 'border-slate-200 bg-white hover:border-blue-200 hover:bg-blue-50/60'
                    }`}
                  >
                    <div className="text-lg mb-1">{method.icon}</div>
                    <div className="text-xs font-semibold text-slate-700 leading-tight">{method.name}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Confirm Selection Button */}
        {selectedPlan && selectedPaymentMethod && (
          <div className="pt-3 border-t border-slate-200 mt-3 text-center">
            <button
              onClick={handleProceedToPayment}
              className="px-8 py-3 bg-green-600 hover:bg-green-700 text-white text-base font-semibold rounded-2xl transition-colors shadow-sm"
            >
              {language === 'en' ? 'Confirm selection' : 'Auswahl bestätigen'}
            </button>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="mt-4 p-3 bg-red-100 border border-red-300 rounded-lg">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

      </div>
    </div>
    </div>
  );
};

export default PlanModal;
